import { PassPurchaseVerificationResponse } from './watercraftPassesSlice';

export const DEFAULT_PASS_POLL_DELAYS_MS = [1000, 2000, 3000];

export type PassPurchaseStatus = 'success' | 'processing' | 'failed';

export interface PassPurchaseResult {
  status: PassPurchaseStatus;
  availableCount?: number;
}

interface ResolvePassPurchaseDeps {
  sessionId: string | null;
  verify: (
    checkoutSessionId: string
  ) => Promise<PassPurchaseVerificationResponse>;
  pollDelaysMs?: number[];
  isCancelled?: () => boolean;
  wait?: (ms: number) => Promise<void>;
}

const defaultWait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Resolves the outcome of a one-off session-pass purchase after the Stripe
 * redirect. Unlike the subscription verify, `verified: true` is success
 * regardless of `credited_count` (a repeat verify is idempotent and returns 0),
 * and a `verified: false` response is not a failure — it means a delayed payment
 * (e.g. BACS) hasn't settled yet, so the passes will land later via the webhook.
 * A verify that only ever errors (404 unknown product / network) is a failure.
 */
export async function resolvePassPurchase({
  sessionId,
  verify,
  pollDelaysMs = DEFAULT_PASS_POLL_DELAYS_MS,
  isCancelled = () => false,
  wait = defaultWait,
}: ResolvePassPurchaseDeps): Promise<PassPurchaseResult> {
  if (!sessionId) {
    return { status: 'failed' };
  }

  // Tracks whether verify ever returned a (not-yet-verified) response, which
  // distinguishes a pending payment (→ processing) from a verify that only
  // errored (→ failed).
  let sawPendingResponse = false;

  const attempt = async (): Promise<PassPurchaseResult | null> => {
    try {
      const result = await verify(sessionId);
      if (result?.verified) {
        return {
          status: 'success',
          availableCount: result.session_pass_counts?.available,
        };
      }
      sawPendingResponse = true;
      return null;
    } catch {
      return null;
    }
  };

  const first = await attempt();
  if (first) {
    return first;
  }

  for (const delay of pollDelaysMs) {
    if (isCancelled()) {
      return { status: 'processing' };
    }
    await wait(delay);
    if (isCancelled()) {
      return { status: 'processing' };
    }
    const next = await attempt();
    if (next) {
      return next;
    }
  }

  return { status: sawPendingResponse ? 'processing' : 'failed' };
}
